import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import { IChatConversation, IChatSettings } from "@models/chat.model";
import { ChatService } from "@services/chat/chat.service";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { ChatImageComponent } from "./chat-image.component";
import { relativeTime } from "./chat-time.util";
import { ConversationComponent } from "./conversation/conversation.component";
import { MessageSettingsDialogComponent } from "./message-settings-dialog.component";

/** Conversations per page of the list (the API's page size). */
const LIST_PAGE_SIZE = 30;
/** How often the conversation list refreshes while the tab is visible. */
const LIST_REFRESH_MS = 30_000;

/**
 * Messages: shoppers' conversations with the store. The list and the open
 * conversation sit side by side; on small screens they stack (list, tap, the
 * conversation with a back button). `?c=<id>` opens a conversation (email
 * digest links).
 */
@Component({
  selector: "fh-messages",
  templateUrl: "./messages.component.html",
  styleUrls: ["./messages.component.scss"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ChatImageComponent,
    ConversationComponent,
  ],
})
export class MessagesComponent implements OnInit, OnDestroy {
  private chatService = inject(ChatService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  readonly relativeTime = relativeTime;

  isMobile = toSignal(
    this.breakpointObserver.observe("(max-width: 767.98px)").pipe(map((r) => r.matches)),
    { initialValue: window.innerWidth < 768 }
  );

  searchControl = new FormControl<string>("", { nonNullable: true });
  search = signal<string>("");
  unreadOnly = signal<boolean>(false);
  conversations = signal<IChatConversation[]>([]);
  loading = signal<boolean>(false);
  loadingMore = signal<boolean>(false);
  hasMore = signal<boolean>(false);
  listError = signal<string | null>(null);
  selectedId = signal<number | null>(null);
  settings = signal<IChatSettings>({ quickReplies: [], awayEnabled: false, awayMessage: "" });

  quickReplies = computed(() => this.settings().quickReplies ?? []);
  /** Small screens: the list is shown over the open conversation (its back button sets it). */
  listOpen = signal<boolean>(false);
  showList = computed(() => !this.isMobile() || !this.selectedId() || this.listOpen());
  showConversation = computed(() => !this.isMobile() || !!this.selectedId());

  private listTimer: ReturnType<typeof setInterval> | null = null;
  private subs = new Subscription();
  /** The in-flight load / load-more, and the background refresh (a load cancels both). */
  private listRequest?: Subscription;
  private refreshRequest?: Subscription;
  /** Bumped by every list (re)load, so a reply for older filters is dropped. */
  private listGen = 0;

  private readonly onVisibility = () => {
    if (document.visibilityState === "visible") {
      this.refreshList();
      this.startListTimer();
    } else {
      this.stopListTimer();
    }
  };

  ngOnInit(): void {
    this.subs.add(
      this.route.queryParamMap.subscribe((params) => {
        const id = Number(params.get("c"));
        this.selectedId.set(Number.isInteger(id) && id > 0 ? id : null);
        this.listOpen.set(false);
      })
    );
    this.subs.add(
      this.searchControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value) => {
          this.search.set(value.trim());
          this.loadList();
        })
    );

    this.loadList();
    this.loadSettings();
    document.addEventListener("visibilitychange", this.onVisibility);
    if (document.visibilityState === "visible") this.startListTimer();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.listRequest?.unsubscribe();
    this.refreshRequest?.unsubscribe();
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopListTimer();
  }

  // ---- List ----------------------------------------------------------------------

  /** The filters a request was made for: a reply for any others is stale. */
  private filterKey(): string {
    return `${this.search()}|${this.unreadOnly() ? 1 : 0}`;
  }

  loadList(): void {
    // A new load wins: cancel any load / load-more / refresh still in flight.
    this.listRequest?.unsubscribe();
    this.refreshRequest?.unsubscribe();
    const gen = ++this.listGen;
    this.loadingMore.set(false);
    this.loading.set(true);
    this.listError.set(null);
    this.listRequest = this.chatService
      .listConversations({ search: this.search(), unread: this.unreadOnly() }, { silent: true })
      .subscribe({
        next: (res) => {
          if (gen !== this.listGen) return;
          this.loading.set(false);
          if (!res.status) {
            this.listError.set(res.message || "Could not load messages.");
            return;
          }
          const list = res.data?.conversations ?? [];
          this.conversations.set(list);
          this.hasMore.set(list.length >= LIST_PAGE_SIZE);
        },
        error: () => {
          if (gen !== this.listGen) return;
          this.loading.set(false);
          this.listError.set("Could not load messages.");
        },
      });
  }

  /**
   * The first page again, merged in front of the rows already loaded: rows it now holds
   * replace their old copies; loaded rows older than its last one stay below (none are
   * dropped just because they were on page 1 before).
   */
  refreshList(): void {
    if (silentSessionExpired()) return;
    if (this.loading() || this.loadingMore()) return;
    this.refreshRequest?.unsubscribe(); // the newest ask wins
    const gen = this.listGen;
    const key = this.filterKey();
    this.refreshRequest = this.chatService
      .listConversations({ search: this.search(), unread: this.unreadOnly() }, { background: true })
      .subscribe({
        next: (res) => {
          // Filters changed (or a load started) since: this reply is for another list.
          if (!res.status || gen !== this.listGen || key !== this.filterKey()) return;
          const first = res.data?.conversations ?? [];
          if (first.length < LIST_PAGE_SIZE) {
            // The whole list fits in one page: it is the list.
            this.conversations.set(first);
            this.hasMore.set(false);
            return;
          }
          const ids = new Set(first.map((c) => c.id));
          const oldest = first[first.length - 1].lastMessageAt;
          const rest = this.conversations().filter(
            (c) => !ids.has(c.id) && c.lastMessageAt <= oldest
          );
          this.conversations.set([...first, ...rest]);
          if (!rest.length) this.hasMore.set(true);
        },
        error: () => {},
      });
  }

  loadMore(): void {
    if (this.loadingMore() || this.loading()) return;
    this.refreshRequest?.unsubscribe();
    const gen = this.listGen;
    const key = this.filterKey();
    this.loadingMore.set(true);
    this.listRequest = this.chatService
      .listConversations(
        {
          search: this.search(),
          unread: this.unreadOnly(),
          offset: this.conversations().length,
        },
        { silent: true }
      )
      .subscribe({
        next: (res) => {
          if (gen !== this.listGen || key !== this.filterKey()) return;
          this.loadingMore.set(false);
          if (!res.status) return;
          const more = res.data?.conversations ?? [];
          const ids = new Set(this.conversations().map((c) => c.id));
          this.conversations.update((list) => [...list, ...more.filter((c) => !ids.has(c.id))]);
          this.hasMore.set(more.length >= LIST_PAGE_SIZE);
        },
        error: () => {
          if (gen === this.listGen) this.loadingMore.set(false);
        },
      });
  }

  toggleUnread(): void {
    this.unreadOnly.update((v) => !v);
    this.loadList();
  }

  clearSearch(): void {
    this.searchControl.setValue("");
  }

  private startListTimer(): void {
    this.stopListTimer();
    this.listTimer = setInterval(() => {
      // Session gone (a background poll was refused): stop polling.
      if (silentSessionExpired()) this.stopListTimer();
      else this.refreshList();
    }, LIST_REFRESH_MS);
  }

  private stopListTimer(): void {
    if (this.listTimer) clearInterval(this.listTimer);
    this.listTimer = null;
  }

  // ---- Selection -----------------------------------------------------------------

  open(conversation: IChatConversation): void {
    this.listOpen.set(false);
    // Read as soon as it opens (the conversation marks it read on the server).
    this.conversations.update((list) =>
      list.map((c) => (c.id === conversation.id ? { ...c, vendorUnread: 0 } : c))
    );
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { c: conversation.id },
      queryParamsHandling: "merge",
    });
  }

  closeConversation(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { c: null },
      queryParamsHandling: "merge",
    });
  }

  onHidden(id: number): void {
    this.conversations.update((list) => list.filter((c) => c.id !== id));
    this.closeConversation();
  }

  // ---- Settings ------------------------------------------------------------------

  private loadSettings(): void {
    this.chatService.getSettings().subscribe({
      next: (res) => {
        if (res.status && res.data) {
          this.settings.set({
            quickReplies: res.data.quickReplies ?? [],
            awayEnabled: !!res.data.awayEnabled,
            awayMessage: res.data.awayMessage ?? "",
          });
        }
      },
      error: () => {},
    });
  }

  openSettings(): void {
    this.dialog
      .open(MessageSettingsDialogComponent, {
        data: this.settings(),
        width: "560px",
        maxWidth: "95vw",
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((saved?: IChatSettings) => {
        if (saved) this.settings.set(saved);
      });
  }
}
